import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ApiOutlined
} from '@ant-design/icons'
import {
  Button,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Space,
  Table,
  message
} from 'antd'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  apiCreateUploadLocation,
  apiDeleteUploadLocation,
  apiGetUploadLocations,
  apiTestUploadLocation,
  apiUpdateUploadLocation
} from '~/api/adminApi'
import { PageHeader } from '~/components/PageHeader'
import { RegionSelect } from '~/components/RegionSelect'
import { showErrors } from '~/utils/helpers'

const key = ['upload-locations']
const UploadLocations = () => {
  const queryClient = useQueryClient()
  const { data = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: async () => await apiGetUploadLocations()
  })
  const [editing, setEditing] = useState<any>(null)
  const [open, setOpen] = useState(false)
  const mutation = useMutation({
    mutationFn: async (values: any) =>
      editing
        ? await apiUpdateUploadLocation(editing.id, values)
        : await apiCreateUploadLocation(values),
    onError: showErrors,
    onSuccess: () => {
      setOpen(false)
      setEditing(null)
      void queryClient.invalidateQueries({ queryKey: key })
      message.success('Saved successfully')
    }
  })
  const remove = useMutation({
    mutationFn: apiDeleteUploadLocation,
    onError: showErrors,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key })
      message.success('Deleted successfully')
    }
  })
  const test = useMutation({
    mutationFn: apiTestUploadLocation,
    onError: showErrors,
    onSuccess: (result) => {
      message.success(result.message)
    }
  })
  return (
    <>
      <PageHeader
        className='radup-page-header'
        title='Upload Locations'
        extra={
          <Button
            type='primary'
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null)
              setOpen(true)
            }}
          >
            New location
          </Button>
        }
      />
      <Table loading={isLoading} dataSource={data} rowKey='id'>
        <Table.Column title='Name' dataIndex='name' />
        <Table.Column title='Bucket' dataIndex='bucket' />
        <Table.Column title='Region' dataIndex='region' />
        <Table.Column
          title='Actions'
          render={(_, row: any) => (
            <Space>
              <Button
                icon={<ApiOutlined />}
                loading={test.isPending}
                onClick={() => {
                  test.mutate(row.id)
                }}
              >
                Test connection
              </Button>
              <Button
                icon={<EditOutlined />}
                onClick={() => {
                  setEditing(row)
                  setOpen(true)
                }}
              />
              <Popconfirm
                title='Delete this upload location?'
                onConfirm={() => {
                  remove.mutate(row.id)
                }}
              >
                <Button danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          )}
        />
      </Table>
      <Drawer
        open={open}
        title={editing ? 'Edit upload location' : 'New upload location'}
        onClose={() => {
          setOpen(false)
          setEditing(null)
        }}
        destroyOnHidden
      >
        <Form
          layout='vertical'
          initialValues={editing ?? {}}
          onFinish={(values) => {
            mutation.mutate(values)
          }}
        >
          <Form.Item name='name' label='Name' rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name='bucket'
            label='S3 bucket'
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name='region' label='Region' rules={[{ required: true }]}>
            <RegionSelect rememberSelection={false} showRegionCode />
          </Form.Item>
          <Button type='primary' htmlType='submit' loading={mutation.isPending}>
            Save
          </Button>
        </Form>
      </Drawer>
    </>
  )
}
export default UploadLocations
